import { useState, useEffect, useCallback, useMemo } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Select from "react-select";
import axiosInstance from "../../../Config/axiosInstance";
import "./AddClientContactsDialog.css";

const initialFormData = {
  name: "",
  position: "",
  phone: "",
  email: "",
  contact_status: "Active",
};

const initialErrors = {
  name: "",
  position: "",
  phone: "",
  email: "",
  contact_status: "",
  submit: "",
};

const EditClientContacts = ({
  isOpen,
  onClose,
  contact,
  clientId,
  onSuccess,
}) => {
  const [contactData, setContactData] = useState(initialFormData);
  const [errors, setErrors] = useState(initialErrors);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Blochează toate controalele din formular cât timp se face submit.
  const disabledAll = useMemo(() => isSubmitting, [isSubmitting]);

  // Opțiunile disponibile pentru statusul contactului.
  const statusOptions = useMemo(
    () => [
      { value: "Active", label: "Active" },
      { value: "Inactive", label: "Inactive" },
    ],
    []
  );

  // Stiluri custom pentru componenta Select.
  const selectMenuStyles = useMemo(
    () => ({
      menuPortal: (base) => ({ ...base, zIndex: 20000 }),
      menu: (base) => ({ ...base, zIndex: 20000 }),
      control: (base, state) => ({
        ...base,
        minHeight: "40px",
        borderRadius: "12px",
        borderWidth: "1.5px",
        borderColor: state.isFocused ? "#3b82f6" : "#e2e8f0",
        boxShadow: state.isFocused
          ? "0 0 0 4px rgba(59, 130, 246, 0.1), 0 4px 8px -4px rgba(59, 130, 246, 0.3)"
          : "0 2px 4px rgba(0, 0, 0, 0.02)",
        "&:hover": {
          borderColor: "#3b82f6",
        },
      }),
      placeholder: (base) => ({
        ...base,
        color: "#94a3b8",
      }),
    }),
    []
  );

  // Când dialogul se deschide și există un contact selectat,
  // formularul este populat cu datele curente ale contactului.
  // În același timp se resetează erorile și starea de submitted.
  useEffect(() => {
    if (contact && isOpen) {
      setContactData({
        name: contact.name || "",
        position: contact.position || "",
        phone: contact.phone || "",
        email: contact.email || "",
        contact_status: contact.contact_status || "Active",
      });
      setErrors(initialErrors);
      setSubmitted(false);
    }
  }, [contact, isOpen]);

  // Gestionează modificarea inputurilor simple și curăță erorile pentru câmpul modificat.
  const handleChange = useCallback((e) => {
    const { name, value } = e.target;

    setContactData((prev) => ({
      ...prev,
      [name]: value,
    }));

    setErrors((prev) => ({
      ...prev,
      [name]: "",
      submit: "",
    }));
  }, []);

  // Gestionează schimbarea statusului din Select și elimină eroarea aferentă câmpului.
  const handleSelectChange = useCallback((selectedOption) => {
    setContactData((prev) => ({
      ...prev,
      contact_status: selectedOption ? selectedOption.value : "Active",
    }));

    setErrors((prev) => ({
      ...prev,
      contact_status: "",
      submit: "",
    }));
  }, []);

  // Validează formularul de editare contact:
  // - name și position obligatorii
  // - phone obligatoriu și în format valid
  // - email obligatoriu și în format valid
  // Actualizează obiectul errors pentru afișare în UI.
  const validateForm = useCallback(() => {
    const newErrors = { ...initialErrors };

    if (!contactData.name.trim()) {
      newErrors.name = "Name is required";
    }

    if (!contactData.position.trim()) {
      newErrors.position = "Position is required";
    }

    if (!contactData.phone.trim()) {
      newErrors.phone = "Phone is required";
    } else if (!/^\+?[0-9]\d{1,19}$/.test(contactData.phone.replace(/\s/g, ""))) {
      newErrors.phone = "Please enter a valid phone number";
    }

    if (!contactData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contactData.email.trim())) {
      newErrors.email = "Invalid email format";
    }

    setErrors((prev) => ({
      ...prev,
      ...newErrors,
      submit: "",
    }));

    return (
      !newErrors.name &&
      !newErrors.position &&
      !newErrors.phone &&
      !newErrors.email
    );
  }, [contactData]);

  // Resetează formularul și închide dialogul.
  // Este folosit atât la cancel, cât și după salvarea cu succes.
  const handleReset = useCallback(() => {
    setContactData(initialFormData);
    setErrors(initialErrors);
    setSubmitted(false);
    onClose?.();
  }, [onClose]);

  // Funcția principală de submit:
  // 1. oprește comportamentul implicit al form-ului
  // 2. marchează formularul ca trimis
  // 3. validează datele
  // 4. construiește payload-ul pentru backend
  // 5. face update contactului prin API
  // 6. apelează callback-ul de succes și închide dialogul
  // În caz de eroare, afișează mesajul în secțiunea submit.
  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      setSubmitted(true);

      if (!validateForm()) {
        return;
      }

      setIsSubmitting(true);

      try {
        const contactToEdit = {
          id: contact.id,
          name: contactData.name.trim(),
          position: contactData.position.trim(),
          phone: contactData.phone.trim(),
          email: contactData.email.trim(),
          contact_status: contactData.contact_status,
          client: clientId,
        };

        const response = await axiosInstance.put(
          `/client-contacts/${contact.id}/`,
          contactToEdit
        );

        if (onSuccess) {
          onSuccess(response.data);
        }

        handleReset();
      } catch (error) {
        console.error("Error editing contact:", error);

        setErrors((prev) => ({
          ...prev,
          submit:
            error?.response?.data?.message ||
            error?.response?.data?.detail ||
            error?.message ||
            "Error editing contact",
        }));
      } finally {
        setIsSubmitting(false);
      }
    },
    [validateForm, contact, contactData, clientId, onSuccess, handleReset]
  );

  // Variabile ajutătoare pentru afișarea erorilor
  // doar după ce utilizatorul încearcă submit-ul.
  const showNameError = submitted && !!errors.name;
  const showPositionError = submitted && !!errors.position;
  const showPhoneError = submitted && !!errors.phone;
  const showEmailError = submitted && !!errors.email;

  // Dacă dialogul nu este deschis sau nu există contact selectat,
  // componenta nu randă nimic.
  if (!isOpen || !contact) return null;

  return (
    <Dialog
      open={isOpen}
      onClose={disabledAll ? undefined : handleReset}
      maxWidth="sm"
      fullWidth
      className="add-client-contacts-dialog"
    >
      <DialogTitle className="accd-title">
        <span className="accd-header-title">Edit Contact Person</span>
      </DialogTitle>

      <DialogContent dividers className="accd-content">
        <form onSubmit={handleSubmit} className="accd-form">
          {/* Secțiunea pentru numele contactului */}
          <div className="accd-form-group">
            <label className="accd-label">
              Name <span style={{ color: "red" }}>*</span>
            </label>
            <input
              type="text"
              name="name"
              value={contactData.name}
              onChange={handleChange}
              disabled={disabledAll}
              className={`accd-input ${showNameError ? "accd-input-error" : ""}`}
              placeholder="Enter contact name"
            />
            {showNameError && (
              <div className="accd-field-error">{errors.name}</div>
            )}
          </div>

          {/* Secțiunea pentru funcția/poziția contactului */}
          <div className="accd-form-group">
            <label className="accd-label">
              Position <span style={{ color: "red" }}>*</span>
            </label>
            <input
              type="text"
              name="position"
              value={contactData.position}
              onChange={handleChange}
              disabled={disabledAll}
              className={`accd-input ${showPositionError ? "accd-input-error" : ""
                }`}
              placeholder="Enter position"
            />
            {showPositionError && (
              <div className="accd-field-error">{errors.position}</div>
            )}
          </div>

          {/* Secțiunea pentru telefon */}
          <div className="accd-form-group">
            <label className="accd-label">
              Phone <span style={{ color: "red" }}>*</span>
            </label>
            <input
              type="text"
              name="phone"
              value={contactData.phone}
              onChange={handleChange}
              disabled={disabledAll}
              className={`accd-input ${showPhoneError ? "accd-input-error" : ""}`}
              placeholder="Enter phone number"
            />
            {showPhoneError && (
              <div className="accd-field-error">{errors.phone}</div>
            )}
          </div>

          {/* Secțiunea pentru email */}
          <div className="accd-form-group">
            <label className="accd-label">
              Email <span style={{ color: "red" }}>*</span>
            </label>
            <input
              type="email"
              name="email"
              value={contactData.email}
              onChange={handleChange}
              disabled={disabledAll}
              className={`accd-input ${showEmailError ? "accd-input-error" : ""}`}
              placeholder="Enter email address"
            />
            {showEmailError && (
              <div className="accd-field-error">{errors.email}</div>
            )}
          </div>

          {/* Secțiunea pentru statusul contactului */}
          <div className="accd-form-group">
            <label className="accd-label">Status</label>
            <Select
              name="contact_status"
              options={statusOptions}
              value={
                statusOptions.find(
                  (option) => option.value === contactData.contact_status
                ) || null
              }
              onChange={handleSelectChange}
              classNamePrefix="accd-react-select"
              placeholder="Select Status"
              isDisabled={disabledAll}
              menuPortalTarget={document.body}
              menuPosition="fixed"
              styles={selectMenuStyles}
            />
          </div>

          {/* Mesaj general de eroare pentru submit */}
          {errors.submit && (
            <div className="accd-submit-error">{errors.submit}</div>
          )}
        </form>
      </DialogContent>

      <DialogActions className="accd-actions">
        {/* Buton de anulare / închidere */}
        <Button
          type="button"
          onClick={handleReset}
          className="accd-btn accd-btn-cancel"
          disabled={disabledAll}
        >
          Cancel
        </Button>

        {/* Buton principal pentru salvarea modificărilor */}
        <Button
          type="submit"
          onClick={handleSubmit}
          variant="contained"
          className="accd-btn accd-btn-create"
          disabled={disabledAll}
          startIcon={
            isSubmitting ? (
              <CircularProgress size={18} className="accd-spinner" />
            ) : null
          }
        >
          {isSubmitting ? "Saving..." : "Update Contact"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditClientContacts;